import { Modal, Page, Text } from '@ui/themed';
import { t } from 'i18next';
import { useCallback, useEffect, useRef } from 'react';
import { SparkSuspense } from '../../../components/spark-suspense';
import { useEnv } from '../../../context/env-context';
import { useUser, withPageProviders } from '../../../context/page-context';
import { useModal } from '../../../hooks/use-modal';
import type { I18nPrefix } from '../../../i18n/prefix';
import { AnalyticsCard } from '../components/cards/analytics-card';
import { CheckoutSettingsCard } from '../components/cards/checkout-settings-card';
import { MyAccountSettingsCard } from '../components/cards/my-account-settings-card';
import { PricingSettingsCard } from '../components/cards/pricing-settings-card';
import { PublishStatusCard } from '../components/cards/publish-status-card';
import { SlotsCard } from '../components/cards/slots-card';
import { TranslationOverridesCard } from '../components/cards/translation-overrides-card';
import { ImportExportCard } from '../components/import-export-card';
import { StorefrontOptionsProvider } from '../context/storefront-options-context';
import { useStorefrontOptions } from '../hooks/use-storefront-options';
import { useUnsavedChangesWarning } from '../hooks/use-unsaved-changes-warning';

const PAGE = 'features:storefront_options.pages.storefront_options_page' satisfies I18nPrefix;

export type StorefrontOptionsPageProps = {
  onDirtyChange?: (isDirty: boolean) => void;
};

export const StorefrontOptionsPage = withPageProviders<StorefrontOptionsPageProps>(
  StorefrontOptionsPageContent,
);

function StorefrontOptionsPageContent({ onDirtyChange }: StorefrontOptionsPageProps) {
  const controls = useStorefrontOptions();
  const { isSparkStaff } = useUser();
  const env = useEnv();

  const {
    isDirty,
    isSaving,
    isBusy,
    canResetToDefaults,
    canDelete,
    resetToDefaults,
    deleteSettings,
  } = controls;

  const {
    isOpen: isPublishModalOpen,
    openModal: openPublishModal,
    closeModal: closePublishModal,
  } = useModal();

  const {
    isOpen: isDiscardModalOpen,
    openModal: openDiscardModal,
    closeModal: closeDiscardModal,
  } = useModal();

  const {
    isOpen: isResetModalOpen,
    openModal: openResetModal,
    closeModal: closeResetModal,
  } = useModal();

  const {
    isOpen: isDeleteModalOpen,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal,
  } = useModal();

  const slotsRef = useRef<HTMLDivElement>(null);

  useUnsavedChangesWarning(isDirty);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const revealSlots = useCallback(() => {
    slotsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, []);

  const publishModalBody = env.environment === 'live' ? 'body_live' : 'body_test';

  function handlePublish() {
    controls.saveAndPublish();
    closePublishModal();
  }

  function handleDiscard() {
    controls.discardChanges();
    closeDiscardModal();
  }

  function handleReset() {
    resetToDefaults();
    closeResetModal();
  }

  function handleDelete() {
    deleteSettings();
    closeDeleteModal();
  }

  return (
    <Page
      title={t(`${PAGE}.header.title`)}
      description={t(`${PAGE}.header.description`)}
      learnMoreUrl="https://docs.sparklayer.io/frontend-integration"
      primaryAction={{
        label: t(`${PAGE}.header.save`),
        onClick: openPublishModal,
        disabled: !isDirty || isBusy,
        loading: isSaving,
      }}
      secondaryActions={[
        {
          label: t(`${PAGE}.header.secondary_actions.discard`),
          icon: 'x',
          onClick: openDiscardModal,
          disabled: !isDirty || isBusy,
        },
        {
          label: t(`${PAGE}.header.secondary_actions.reset`),
          icon: 'refresh',
          onClick: openResetModal,
          disabled: !canResetToDefaults || isBusy,
        },
        ...(isSparkStaff
          ? [
              {
                label: t(`${PAGE}.header.secondary_actions.delete`),
                icon: 'delete' as const,
                onClick: openDeleteModal,
                disabled: !canDelete || isBusy,
              },
            ]
          : []),
      ]}
      narrow
    >
      <SparkSuspense
        isLoading={controls.isLoading}
        isError={controls.isError}
        onReload={controls.refetch}
        errorMessage={t('errors:generic')}
      >
        <StorefrontOptionsProvider value={controls}>
          <PublishStatusCard />
          <ImportExportCard />
          <CheckoutSettingsCard />
          <MyAccountSettingsCard onRevealSlots={revealSlots} />
          <TranslationOverridesCard />
          <PricingSettingsCard />
          <div ref={slotsRef}>
            <SlotsCard />
          </div>
          <AnalyticsCard />
        </StorefrontOptionsProvider>
      </SparkSuspense>

      <Modal
        isOpen={isPublishModalOpen}
        onClose={closePublishModal}
        primaryAction={{ text: t(`${PAGE}.publish_modal.confirm`), onClick: handlePublish }}
        secondaryAction={{ text: t(`${PAGE}.publish_modal.cancel`), onClick: closePublishModal }}
      >
        <Modal.Header>{t(`${PAGE}.publish_modal.title`)}</Modal.Header>
        <Modal.Body>
          <Text>{t(`${PAGE}.publish_modal.${publishModalBody}`)}</Text>
        </Modal.Body>
      </Modal>

      <Modal
        isOpen={isDiscardModalOpen}
        onClose={closeDiscardModal}
        primaryAction={{
          text: t(`${PAGE}.discard_modal.confirm`),
          danger: true,
          onClick: handleDiscard,
        }}
        secondaryAction={{
          text: t(`${PAGE}.discard_modal.cancel`),
          onClick: closeDiscardModal,
        }}
      >
        <Modal.Header>{t(`${PAGE}.discard_modal.title`)}</Modal.Header>
        <Modal.Body>
          <Text>{t(`${PAGE}.discard_modal.body`)}</Text>
        </Modal.Body>
      </Modal>

      <Modal
        isOpen={isResetModalOpen}
        onClose={closeResetModal}
        primaryAction={{
          text: t(`${PAGE}.reset_modal.confirm`),
          danger: true,
          onClick: handleReset,
        }}
        secondaryAction={{ text: t(`${PAGE}.reset_modal.cancel`), onClick: closeResetModal }}
      >
        <Modal.Header>{t(`${PAGE}.reset_modal.title`)}</Modal.Header>
        <Modal.Body>
          <Text>{t(`${PAGE}.reset_modal.body`)}</Text>
        </Modal.Body>
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        primaryAction={{
          text: t(`${PAGE}.delete_modal.confirm`),
          danger: true,
          onClick: handleDelete,
        }}
        secondaryAction={{ text: t(`${PAGE}.delete_modal.cancel`), onClick: closeDeleteModal }}
      >
        <Modal.Header>{t(`${PAGE}.delete_modal.title`)}</Modal.Header>
        <Modal.Body>
          <Text>{t(`${PAGE}.delete_modal.body`)}</Text>
        </Modal.Body>
      </Modal>
    </Page>
  );
}
